import React, { useContext, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { isEmpty, noop } from "lodash";
import { observer } from "mobx-react-lite";
import { useEffectOnce, useForceUpdate } from "@lib-shared/hooks";
import { BusinessPage, MenuArea, Messages } from "core/constants";
import { AppContext } from "core/context";
import { Feature, isEnabled } from "core/features";
import { logApiError } from "core/lib";
import { dateWrapper } from "core/lib/date.wrapper";
import { ItemType } from "core/stores/statements-and-invoices.store";
import StatementsAndInvoicesStoreManager from "core/stores/statements-and-invoices.store.manager";
import { ModalAlert } from "components/common-ui";
import Filter from "./components/filter";
import ItemRows from "./components/item-rows";
import Pager from "components/pager";
import { getItemTypes } from "./shared";

type StatementsAndInvoices = {
    dataManager: StatementsAndInvoicesStoreManager
}

const StatementsAndInvoices = observer((props: StatementsAndInvoices) => {
    const initialDateRangeInMonths = 6;

    let endDate: Date | string = new Date();
    let startDate: Date | string = dateWrapper().addMonths(0 - initialDateRangeInMonths).date;
    const maxDate = dateWrapper().addMonths(2).date;

    const abortController = useRef({
        statements: new AbortController(),
        invoices: new AbortController()
    });
    const appContext = useContext(AppContext);
    const navigate = useNavigate();

    const forceUpdate = useForceUpdate();

    const [state, setState] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [loadingDataType, setLoadingDataType] = useState(ItemType.statement);

    const { dataManager } = props;
    dataManager.initialize(abortController.current.statements, abortController.current.invoices);

    const store = dataManager.store;

    const getInitialState = () => {
        return {
            itemType: ItemType.statement,
            startDate: new Date(startDate),
            endDate: new Date(endDate),
            maxDate: new Date(maxDate),
        };
    };

    const onLoad = async () => {
        const title = "Statements and Invoices";
        appContext.updateTitle(title);
        appContext.updateHeader(title);

        const initialState = getInitialState();

        setState(initialState);

        if (!isEnabled(Feature.dashboard_statements)) {
            setState(null);
            ModalAlert.permissionsError(() => navigate(BusinessPage.dashboard));
            return;
        }

        await getItems(initialState);

        return () => {
            abortController.current.statements.abort();
            abortController.current.invoices.abort();
        };
    }

    useEffectOnce(() => {
        appContext.updateArea(MenuArea.manage);

        onLoad();
    });

    const abort = () => {
        console.log(`Cancelling ${loadingDataType}`);

        const controller = loadingDataType === ItemType.invoice
            ? abortController.current.invoices
            : abortController.current.statements;

        controller.abort();

        if (loadingDataType === ItemType.invoice) {
            abortController.current.invoices = new AbortController();
        } else {
            abortController.current.statements = new AbortController();
        }
    }

    const getItems = async (currentState: any = null) => {
        currentState = currentState ?? state;
        const type = currentState.itemType;

        if (isLoading) {
            abort();
        }

        setLoadingDataType(currentState.itemType);

        if (currentState.startDate === "" || currentState.endDate === "") {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        startDate = dateWrapper(currentState.startDate).firstOfMonth().date;
        endDate = dateWrapper(currentState.endDate).firstOfMonth().date;

        try {
            await dataManager.getItems(type, startDate, endDate);

            setIsLoading(false);

            forceUpdate();
        } catch (error) {
            logApiError(error);

            console.log("Error getting amount", error);

            handleErrorGettingItems(error, type);

            setIsLoading(false);
        }
    }

    const handleErrorGettingItems = (ex: any, type = state.itemType) => {
        const pluralType = getItemTypes(type);

        const handleErrorMessage = (error = null) => {
            if (error === null) {
                return;
            }

            const message = `Error getting ${pluralType}`;

            ModalAlert.error(message);

            console.log(message, error);

            store.updateItems([]);
        }

        const response = ex?.response;

        const data = response?.data;

        let error = data?.exception || data?.errorDescription;

        if (error?.includes("System.")) {
            error = Messages.apiError;
        }

        if (response?.status === 404) {
            store.updateItems([]);
            error = `No ${pluralType} found for this date range`;
            setIsLoading(false);
            forceUpdate();
            return;
        }

        if (error != null) {
            console.log(data);
            setErrorMessage(error);
            handleErrorMessage(error);
            return;
        }

        handleErrorMessage(ex);
    }

    const selectType = (type: string) => {
        handleFilterChange("itemType", type)
    }

    const handleFilterChange = (controlName: string, newValue: string, defaultValue = "", autoUpdate = true) => {
        // console.log("handleFilter", newValue);

        try {
            const newState = {
                ...state,
                autoUpdate: autoUpdate,
                [controlName]: newValue ?? defaultValue
            };

            setState(newState)

            getItems(newState);
        } catch (error) {
            console.error(error);
        }
    }

    const ItemFilter = ({ type }: { type: string }) => {
        const title = type.toString() + "s";
        const isActive = state.itemType === type;

        return (
            <li className={isActive ? "active" : ""} onClick={() => isActive ? noop() : selectType(type)}>{title}</li>
        )
    }

    if (isEmpty(state)) {
        return null;
    }

    return (
        <>
            <section id="statements" className={"floaty-form statements-container " + (isLoading ? "is-loading" : "")}>
                <h1>Statements and Invoices</h1>

                <ul>
                    <ItemFilter type={ItemType.statement} />
                    <ItemFilter type={ItemType.invoice} />
                </ul>

                <form className="filter floating">
                    <Filter
                        state={state}
                        isLoading={isLoading}
                        handleChange={handleFilterChange} />
                </form>

                <ItemRows
                    currentItems={store.pageItems}
                    isLoading={isLoading}
                    errorMessage={errorMessage}
                    type={loadingDataType}
                    dataManager={dataManager} />
            </section>

            <Pager
                isLoading={isLoading}
                currentItems={store.pageItems}
                pageCount={store.numPages}
                currentPage={store.currentPage}
                handlePageClick={(e: any) => {
                    store.updateCurrentPage(e.selected);
                    forceUpdate();
                }} />
        </>
    );
});

export default StatementsAndInvoices;
